/**
 * AdminLoginActivityPage.js — Beta cohort login activity (2026-05-29)
 *
 * Renders two surfaces:
 *  1. Per-user list with a denormalised "last seen" chip (date, country/city,
 *     device/browser/os). Click a row → expands inline to that user's last 30
 *     login events.
 *  2. Recent-activity feed (last 200 events across everyone), newest first.
 *
 * Source: GET /api/admin/login-activity → { users, recent_events }
 *         GET /api/admin/login-activity/{user_id} → { events }
 *
 * Read-only — no mutations. Lives on /admin/login-activity, linked from the
 * Admin Accounts header.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, LogOut, Settings, MapPin, Monitor, Smartphone, Tablet, Bot } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";

const DEVICE_ICON = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  bot: Bot,
};

function fmtAgo(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function fmtFull(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function GeoChip({ country, city }) {
  if (!country || country === "?") return <span className="text-zinc-400 text-[12px]">unknown</span>;
  if (country === "Local") return <span className="text-zinc-500 text-[12px]">local network</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[12px] text-zinc-700">
      <MapPin className="w-3 h-3" strokeWidth={1.5} />
      {city ? `${city}, ${country}` : country}
    </span>
  );
}

function DeviceChip({ device, browser, os }) {
  const Icon = DEVICE_ICON[device] || Monitor;
  const parts = [];
  if (browser && browser !== "?") parts.push(browser);
  if (os && os !== "?") parts.push(os);
  return (
    <span className="inline-flex items-center gap-1 text-[12px] text-zinc-700">
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {parts.length ? parts.join(" · ") : (device || "?")}
    </span>
  );
}

function UserRow({ user, expanded, onToggle }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expanded || events !== null) return;
    setLoading(true);
    API.get(`/admin/login-activity/${user.user_id}`, { params: { limit: 30 } })
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [expanded, events, user.user_id]);

  return (
    <>
      <tr
        className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors"
        onClick={onToggle}
        data-testid={`login-user-row-${user.user_id}`}
      >
        <td className="px-3 py-3 align-top w-6">
          {expanded
            ? <ChevronDown className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
            : <ChevronRight className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />}
        </td>
        <td className="px-3 py-3 align-top">
          <div className="text-[13px] font-medium text-zinc-900">{user.email}</div>
          {user.first_name && <div className="text-[11px] text-zinc-500">{user.first_name}{user.tier ? ` · ${user.tier}` : ""}</div>}
        </td>
        <td className="px-3 py-3 align-top whitespace-nowrap">
          <div className="text-[13px] text-zinc-900" title={fmtFull(user.last_login_at)}>
            {user.last_login_at ? fmtAgo(user.last_login_at) : <span className="text-zinc-400">never</span>}
          </div>
        </td>
        <td className="px-3 py-3 align-top">
          <GeoChip country={user.last_login_country} city={user.last_login_city} />
        </td>
        <td className="px-3 py-3 align-top">
          <DeviceChip device={user.last_login_device} browser={user.last_login_browser} os={user.last_login_os} />
        </td>
        <td className="px-3 py-3 align-top">
          <span className="text-[12px] text-zinc-600 font-mono">{user.last_login_ip || "—"}</span>
        </td>
        <td className="px-3 py-3 align-top text-right">
          <span className="text-[12px] text-zinc-700">{user.login_count}</span>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-zinc-100 bg-zinc-50/50">
          <td></td>
          <td colSpan={6} className="px-3 py-3">
            {loading ? (
              <div className="text-[12px] text-zinc-500">Loading login history…</div>
            ) : !events || events.length === 0 ? (
              <div className="text-[12px] text-zinc-500">No login events recorded yet for this user.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-zinc-500 text-left">
                      <th className="pb-1 font-normal">When</th>
                      <th className="pb-1 font-normal">Where</th>
                      <th className="pb-1 font-normal">Device</th>
                      <th className="pb-1 font-normal">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e, i) => (
                      <tr key={i} className="border-t border-zinc-200/60">
                        <td className="py-1.5 pr-3 text-zinc-700 whitespace-nowrap" title={fmtFull(e.login_at)}>
                          {fmtAgo(e.login_at)}
                        </td>
                        <td className="py-1.5 pr-3"><GeoChip country={e.country} city={e.city} /></td>
                        <td className="py-1.5 pr-3"><DeviceChip device={e.device} browser={e.browser} os={e.os} /></td>
                        <td className="py-1.5 pr-3 font-mono text-zinc-600">{e.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminLoginActivityPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [tab, setTab] = useState("users"); // "users" | "feed"

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    API.get("/admin/login-activity", { params: { limit: 200 } })
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => { if (mounted) setData({ users: [], recent_events: [] }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-login-activity-page">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="block" data-testid="login-activity-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="login-activity-header-logo" />
            </Link>
            <BetaBadge />
            <span className="text-[11px] font-medium uppercase tracking-widest text-amber-700 border border-amber-700/30 px-1.5 py-0.5 leading-none">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/accounts" className="text-[13px] text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="back-to-accounts-link">Accounts</Link>
            <Link to="/admin/account-requests" className="text-[13px] text-zinc-900 hover:text-zinc-950 transition-colors">Requests</Link>
            <Link to="/settings/profile" className="text-zinc-900 hover:text-zinc-950 transition-colors"><Settings className="w-4 h-4" strokeWidth={1.5} /></Link>
            <button onClick={() => { logout(); navigate("/"); }} className="text-zinc-900 hover:text-zinc-950 transition-colors"><LogOut className="w-4 h-4" strokeWidth={1.5} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading">Login activity</h1>
          <p className="text-sm text-zinc-900 mt-1">Who's signing in, when, from where. Click a user to expand their last 30 logins.</p>
        </div>

        <div className="flex items-center gap-1 mb-4 border-b border-zinc-200">
          <button
            onClick={() => setTab("users")}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === "users" ? "border-amber-700 text-zinc-950" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}
            data-testid="login-activity-users-tab"
          >
            Users{data ? ` · ${data.users.length}` : ""}
          </button>
          <button
            onClick={() => setTab("feed")}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === "feed" ? "border-amber-700 text-zinc-950" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}
            data-testid="login-activity-feed-tab"
          >
            Recent activity{data ? ` · ${data.recent_events.length}` : ""}
          </button>
        </div>

        {loading && <div className="text-sm text-zinc-500">Loading login activity…</div>}

        {!loading && data && tab === "users" && (
          <div className="bg-white border border-zinc-200">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2 w-6"></th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Last seen</th>
                  <th className="px-3 py-2">Where</th>
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2 text-right">Logins</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <UserRow
                    key={u.user_id}
                    user={u}
                    expanded={expandedId === u.user_id}
                    onToggle={() => setExpandedId(expandedId === u.user_id ? null : u.user_id)}
                  />
                ))}
              </tbody>
            </table>
            {data.users.length === 0 && <div className="px-3 py-6 text-[13px] text-zinc-500">No users yet.</div>}
          </div>
        )}

        {!loading && data && tab === "feed" && (
          <div className="bg-white border border-zinc-200">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Where</th>
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_events.map((e, i) => (
                  <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`login-event-row-${i}`}>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <div className="text-[13px] text-zinc-900" title={fmtFull(e.login_at)}>{fmtAgo(e.login_at)}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-[13px] text-zinc-900">{e.email}</div>
                    </td>
                    <td className="px-3 py-2 align-top"><GeoChip country={e.country} city={e.city} /></td>
                    <td className="px-3 py-2 align-top"><DeviceChip device={e.device} browser={e.browser} os={e.os} /></td>
                    <td className="px-3 py-2 align-top">
                      <span className="text-[12px] text-zinc-600 font-mono">{e.ip || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.recent_events.length === 0 && <div className="px-3 py-6 text-[13px] text-zinc-500">No login events recorded yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
